import { Injectable } from "@angular/core";
import { HttpClient, HttpHeaders } from "@angular/common/http";
import { BehaviorSubject, EMPTY, Observable, throwError } from "rxjs";
import { catchError, map, tap } from "rxjs/operators";
import { Storage } from "@ionic/storage-angular";
import { AuthState } from "../../entities/AuthState";
import { API_URL } from "../../../environments/environment";
import { AuthUser } from "../../entities/AuthUser";
import { AccountsService } from "../accounts/accounts.service";
import { TodayAnniversaries } from "../../entities/TodayAnniversaries";
import { ColleaguesService } from "../colleagues/colleagues.service";
import moment from "moment";
import * as Sentry from "@sentry/browser";
import { Router } from "@angular/router";

export const authStateKey = "authStateTest";
export const todayAnniversariesKey = "anniversaries";

@Injectable({
    providedIn: "root"
})
export class AuthService {
    authState = new BehaviorSubject<AuthState>(
        new AuthState(false, false, null, null, null, null, 1, 1, true)
    );

    userProfileState = new BehaviorSubject<AuthUser>(new AuthUser(null));

    todayAnniversaries = new BehaviorSubject<TodayAnniversaries>(
        new TodayAnniversaries(null)
    );

    requestLoginUrl = `${API_URL}/v2/auth/request-login`;
    twoFactorPinLogin = `${API_URL}/v2/auth/two-fa-pin-login`;
    loginUrl = `${API_URL}/v2/auth/login`;
    setPinUrl = `${API_URL}/v2/auth/reset-pin`;
    clearPinUrl = `${API_URL}/v2/auth/clear-pin`;
    refreshTokenUrl = `${API_URL}/v2/auth/refresh-token`;
    verifyPinUrl = `${API_URL}/v2/auth/verify-pin`;
    logOutUrl = `${API_URL}/v2/auth/logout`;

    constructor(
        private http: HttpClient,
        private storage: Storage,
        private accountsService: AccountsService,
        private colleaguesService: ColleaguesService,
        private router: Router
    ) {}

    async setAuthState() {
        let storageAuthState = await this.storage.get(authStateKey);
        let storageTodayAnniversariesState = await this.storage.get(
            todayAnniversariesKey
        );
        storageAuthState = storageAuthState || AuthState.makeEmpty();
        storageTodayAnniversariesState =
            storageTodayAnniversariesState || new TodayAnniversaries(null);
        this.todayAnniversaries.next(
            new TodayAnniversaries(storageTodayAnniversariesState)
        );

        const authState = AuthState.makeFromObject(storageAuthState);
        const userProfileState = storageAuthState.authUser
            ? new AuthUser(storageAuthState.authUser)
            : new AuthUser(null);

        this.authState.next(authState);
        this.userProfileState.next(userProfileState);

        // The stored profile is only as fresh as the last login, so pick up
        // changes made since then (division, employee function, language,
        // permissions). Deliberately not awaited: startup continues on the
        // stored profile while the account request is in flight, and keeps
        // running on it when the request fails.
        if (authState.isAuthenticated()) {
            this.refreshUserProfile()
                .pipe(
                    catchError((error: any) => {
                        console.error(error);
                        Sentry.captureException(error, {
                            extra: {
                                message:
                                    "Failed to refresh the profile on startup"
                            }
                        });

                        return EMPTY;
                    })
                )
                .subscribe();
        }
    }

    /**
     * Performs a full logout by:
     * - Resetting auth state
     * - Making logout HTTP request
     * - Navigating to login page
     * - Preventing browser back navigation
     */
    completeLogout() {
        const authState = AuthState.makeEmpty();
        authState.authChanged = true;
        this.authState.next(authState);

        this.http
            .post(this.logOutUrl, {})
            .pipe(
                catchError((err: any) => {
                    if (err.status === 401) {
                        // Token is invalid, do nothing, just clear auth state
                        return EMPTY;
                    }

                    return throwError(() => err);
                })
            )
            .subscribe();

        this.router
            .navigate(["/request-login"], {
                replaceUrl: true
            })
            .then(() => {
                window.history.pushState(null, "", window.location.href);
                window.onpopstate = () => {
                    window.history.pushState(null, "", window.location.href);
                };
            });
    }

    /**
     * Similar to completeLogout but skips the HTTP request.
     * Used for forcing user logout without server notification.
     */
    forceLogout() {
        const authState = AuthState.makeEmpty();
        authState.authChanged = true;
        this.authState.next(authState);

        this.router
            .navigate(["/request-login"], {
                replaceUrl: true
            })
            .then(() => {
                window.history.pushState(null, "", window.location.href);
                window.onpopstate = () => {
                    window.history.pushState(null, "", window.location.href);
                };
            });
    }

    /**
     * Handles logout based on authentication state.
     * - If no token exists, does nothing
     * - If user has two-factor token, only clears token and auth state
     * - Otherwise performs complete logout
     * - Clears Sentry user context
     */
    logout() {
        const authState = this.authState.getValue();

        if (!authState.token) {
            return;
        }

        if (!authState.hasTwoFactorToken()) {
            this.completeLogout();
            return;
        }

        this.http
            .post(this.logOutUrl, {})
            .pipe(
                catchError((err: any) => {
                    console.log(JSON.stringify(err));
                    if (err.status === 401) {
                        // Token is invalid, do nothing, just clear auth state
                        return EMPTY;
                    }

                    return throwError(() => err);
                })
            )
            .subscribe();

        authState.setToken(null);
        authState.setAuth(false);
        Sentry.setUser(null);
        this.authState.next(authState);
    }

    requestLogin(phone: string, type = "sms"): Observable<any> {
        const params = {
            phone,
            type,
            device_token: this.authState.getValue().deviceAppToken
        };

        return this.http.post(`${this.requestLoginUrl}`, params);
    }

    setPhoneState(phone: string) {
        const authState = this.authState.getValue();
        authState.setPhone(phone);
        this.authState.next(authState);
    }

    loginWithSms(phone: string, pin: string) {
        const params = {
            phone,
            pin,
            device_token: this.authState.getValue().deviceAppToken
        };

        return this.http.post(`${this.twoFactorPinLogin}`, params).pipe(
            tap((res: any) => {
                const authState = this.authState.getValue();
                authState.setTwoFactorToken(res.token);
                authState.setPinState(res.has_personal_pin);
                this.authState.next(authState);
            })
        );
    }

    setNewPinState(pin: string) {
        const authState = this.authState.getValue();
        authState.setPin(pin);
        this.authState.next(authState);
    }

    clearPinCodes() {
        const authState = this.authState.getValue();
        authState.setPin(null);
        authState.setPinState(false);
        this.authState.next(authState);
    }

    forgotPin() {
        const httpOptions = {
            headers: new HttpHeaders({
                Authorization: `Bearer ${this.authState.getValue().twoFactorToken}`
            })
        };

        return this.http
            .put(`${this.clearPinUrl}`, {}, httpOptions)
            .pipe(
                tap((res: any) => {
                    this.completeLogout();
                })
            )
            .subscribe();
    }

    setNewPin(pin: string) {
        const httpOptions = {
            headers: new HttpHeaders({
                Authorization: `Bearer ${this.authState.getValue().twoFactorToken}`
            })
        };

        return this.http.put(`${this.setPinUrl}`, { pin }, httpOptions).pipe(
            tap((res: any) => {
                const authState = this.authState.getValue();
                const authUser = new AuthUser(res.user);
                authState.setToken(res.token);
                authState.setAuth(true);
                authState.setAuthUserState(authUser);
                authState.setTokenExpiration(res.expires);
                authState.setRefreshTokenExpiration(res.refreshExpires);
                this.authState.next(authState);
                this.userProfileState.next(authUser);
            })
        );
    }

    setPinState(state: boolean) {
        const authState = this.authState.getValue();
        authState.setPinState(true);
        authState.setPin(null);
        this.authState.next(authState);
    }

    login(phone: string, pin: string) {
        const params = {
            phone,
            pin
        };

        return this.http.post(`${this.loginUrl}`, params).pipe(
            tap((res: any) => {
                const authState = this.authState.getValue();
                const authUser = new AuthUser(res.user);
                authState.setToken(res.token);
                authState.setAuth(true);
                authState.setAuthUserState(authUser);
                Sentry.setUser({
                    id: authUser.id,
                    email: authUser.email,
                    name: authUser.name
                });
                authState.setTokenExpiration(res.expires);
                authState.setRefreshTokenExpiration(res.refreshExpires);
                this.authState.next(authState);
                this.userProfileState.next(authUser);
            })
        );
    }

    /**
     * Refreshes the profile in the auth state.
     *
     * Returns a cold observable so callers can await the refreshed profile.
     * Subscribing here would hand back a Subscription, which callers cannot
     * await, leaving them to act on a profile that is still in flight.
     */
    refreshUserProfile(): Observable<AuthUser> {
        return this.accountsService.profile().pipe(
            map((data) => new AuthUser(data)),
            tap((authUser: AuthUser) => this.updateUserDataState(authUser))
        );
    }

    /**
     * Re-emits the current auth state as changed.
     *
     * AuthState.setAuthUserState() deliberately leaves authChanged alone, so a
     * refreshed profile on its own does not wake the AppComponent pipeline.
     * Use this to resume a boot that was interrupted, without a round trip
     * through storage that could still hold the previous profile.
     */
    resumeAuthState() {
        const authState = this.authState.getValue();
        authState.authChanged = true;
        this.authState.next(authState);
    }

    refreshToken() {
        return this.http.post(`${this.refreshTokenUrl}`, {}).pipe(
            tap((res: any) => {
                const authState = this.authState.getValue();
                authState.setToken(res.token);
                authState.setTokenExpiration(res.expires);
                authState.setRefreshTokenExpiration(res.refreshExpires);
                this.authState.next(authState);
            }),
            catchError((err: any) => {
                if (err.status === 401) {
                    this.logout();
                    return EMPTY;
                }

                return throwError(() => err);
            })
        );
    }

    isAuthenticated() {
        return this.authState.getValue().isAuthenticated();
    }

    isUserActive() {
        return this.authState.getValue().authUser.is_active;
    }

    hasUserValidDeactivation() {
        return (
            this.authState.getValue().authUser?.has_valid_deactivation || false
        );
    }

    isPinSet() {
        const authState = this.authState.getValue();

        return authState.hasSetPin();
    }

    verifyPin(pin) {
        return this.http.post(`${this.verifyPinUrl}`, { pin });
    }

    fetchAuthUser(): AuthUser {
        const authState = this.authState.getValue();

        return authState.authUser;
    }

    updateUserDataState(authUser: AuthUser) {
        const authState = this.authState.getValue();
        authState.setAuthUserState(authUser);
        this.authState.next(authState);
        this.userProfileState.next(authUser);
    }

    /**
     * Persists the current auth state and hands back the pending write.
     *
     * The AppComponent pipeline stores the state on every emission, but does
     * not expose that write. Callers that reload the page afterwards need it,
     * otherwise the reload can overtake the write and the app comes back on
     * the profile the user just replaced.
     */
    persistAuthState(): Promise<any> {
        return this.storage.set(authStateKey, this.authState.getValue());
    }

    updateTodayAnniversaries() {
        const comparatorYear = moment().format("MM-DD");
        const comparatorHalfYear = moment()
            .subtract("months", 6)
            .format("MM-DD");

        this.colleaguesService.fetchAnniversaries().subscribe((data) => {
            const date = moment().format("YYYY-MM-DD");
            const current = this.todayAnniversaries.getValue();

            current.addItems(
                date,
                data.current_week.filter((d) => {
                    return [comparatorYear, comparatorHalfYear].includes(
                        moment(d.employment_start_date).format("MM-DD")
                    );
                })
            );

            this.todayAnniversaries.next(current);
            this.storage.set(
                todayAnniversariesKey,
                JSON.parse(JSON.stringify(current))
            );
        });
    }

    markTodayAnniversariesAsRead() {
        const current = this.todayAnniversaries.getValue();
        current.isNotified = true;
        this.todayAnniversaries.next(current);
        this.storage.set(
            todayAnniversariesKey,
            JSON.parse(JSON.stringify(current))
        );
    }
}
